import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../config";
import PageHeader from "./PageHeader";
import { useNavigate } from "react-router-dom";

export default function BackofficeAdmin({ isDarkMode, userRole, currentUserId }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [message, setMessage] = useState("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/users?userId=${currentUserId}`);
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const supportUsers = users.filter(u => u.role === "podpora");
  const regularUsers = users.filter(u => u.role !== "podpora" && u.role !== "superadmin");

  const handleAssignSupport = async (userId, supportUserId) => {
    setSaving(prev => ({ ...prev, [userId]: true }));
    try {
      const res = await fetch(`${API_URL}/users/${userId}?userId=${currentUserId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ supportUserId: supportUserId || null }),
      });
      if (!res.ok) throw new Error("Chyba serveru");
      setUsers(prev => prev.map(u =>
        u._id === userId ? { ...u, supportUserId: supportUserId || null } : u
      ));
      setMessage("Uloženo ✓");
      setTimeout(() => setMessage(""), 2000);
    } catch {
      setMessage("Chyba při ukládání.");
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  if (userRole !== "admin") return null;

  const bg = isDarkMode ? "bg-gray-900" : "bg-gray-100";
  const card = isDarkMode ? "bg-gray-800" : "bg-white";
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const subtext = isDarkMode ? "text-gray-400" : "text-gray-500";
  const border = isDarkMode ? "border-gray-700" : "border-gray-200";
  const th = isDarkMode ? "bg-gray-700 text-gray-200" : "bg-blue-50 text-blue-900";

  return (
    <div className={`min-h-screen bg-gradient-to-br from-gray-100 to-blue-100 dark:from-gray-800 dark:to-gray-900 py-10`}>
      <div className="w-[98vw] max-w-[1800px] mx-auto px-4">
        <PageHeader
          title="Backoffice – přiřazení uživatelů podpory"
          isDarkMode={isDarkMode}
          actions={
            <button
              onClick={() => navigate("/users")}
              className="px-4 py-2 rounded-lg bg-gray-500 hover:bg-gray-600 text-white font-semibold shadow-md transition"
            >
              ← Admin panel
            </button>
          }
        />

        {message && (
          <div className={`mb-4 px-4 py-2 rounded-lg text-sm font-medium ${
            message.includes("Chyba") ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
          }`}>
            {message}
          </div>
        )}

        <div className={`${card} rounded-lg shadow border ${border} overflow-hidden`}>
          <div className={`px-6 py-4 border-b ${border}`}>
            <h2 className={`font-bold text-base ${text}`}>
              Přiřazení uživatele podpory
            </h2>
            <p className={`text-sm mt-1 ${subtext}`}>
              Přednastavený uživatel podpory se automaticky zobrazí při vytváření nového požadavku.
            </p>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent mx-auto mb-3"></div>
              <p className={subtext}>Načítám uživatele...</p>
            </div>
          ) : regularUsers.length === 0 ? (
            <div className={`p-8 text-center ${subtext}`}>Žádní uživatelé.</div>
          ) : (
            <table className="min-w-full">
              <thead>
                <tr className={th}>
                  <th className="py-3 px-5 text-left text-sm font-semibold">Uživatel</th>
                  <th className="py-3 px-5 text-left text-sm font-semibold">Role</th>
                  <th className="py-3 px-5 text-left text-sm font-semibold">Uživatel podpory</th>
                  <th className="py-3 px-5 text-left text-sm font-semibold w-24">Uložit</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${border}`}>
                {regularUsers.map(user => (
                  <tr key={user._id} className={isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"}>
                    <td className={`py-3 px-5 font-medium ${text}`}>{user.name}</td>
                    <td className={`py-3 px-5 text-sm ${subtext}`}>{user.role}</td>
                    <td className="py-3 px-5">
                      <select
                        value={user.supportUserId?._id || user.supportUserId || ""}
                        onChange={e => handleAssignSupport(user._id, e.target.value)}
                        className={`w-full px-3 py-1.5 border rounded-lg text-sm ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <option value="">— bez přiřazení —</option>
                        {supportUsers.map(su => (
                          <option key={su._id} value={su._id}>
                            {su.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 px-5">
                      {saving[user._id] && (
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {supportUsers.length === 0 && !loading && (
          <div className={`mt-4 px-4 py-3 rounded-lg border text-sm ${
            isDarkMode ? "bg-yellow-900/30 border-yellow-700 text-yellow-300" : "bg-yellow-50 border-yellow-300 text-yellow-700"
          }`}>
            ⚠️ Žádní uživatelé s rolí „podpora" neexistují. Nejprve vytvořte uživatele s rolí <strong>podpora</strong>.
          </div>
        )}
      </div>
    </div>
  );
}
